/**
 * Employee avatar — initials in a rounded-lg (12px) box with a soft shadow,
 * matching the payroll prototype's `.av` treatment. No photo upload in
 * Increment 1 (the prototype's pravatar photos are mock-only; real photo
 * upload is a documented follow-on).
 *
 * @see prototype/payroll/payroll.css `.av`
 */
import { cn } from "@flint/ui";

export type EmployeeAvatarSize = "sm" | "md" | "lg";

const SIZE: Record<EmployeeAvatarSize, string> = {
  sm: "h-8 w-8 text-xs",
  md: "h-10 w-10 text-sm",
  lg: "h-16 w-16 text-xl",
};

export function initialsFor(name: string): string {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join("") || "?"
  );
}

export function EmployeeAvatar({
  name,
  size = "md",
  photoUrl = null,
  className,
}: {
  name: string;
  size?: EmployeeAvatarSize;
  /** Resolved public URL of the profile picture; falls back to initials. */
  photoUrl?: string | null;
  className?: string;
}) {
  if (photoUrl) {
    return (
      <img
        src={photoUrl}
        alt=""
        aria-hidden
        className={cn(
          "shrink-0 rounded-lg object-cover shadow-card ring-1 ring-line",
          SIZE[size],
          className,
        )}
      />
    );
  }
  return (
    <span
      aria-hidden
      className={cn(
        "inline-grid shrink-0 place-items-center rounded-lg bg-gradient-to-br from-sky-500 to-sky-700 font-bold text-white shadow-card",
        SIZE[size],
        className,
      )}
    >
      {initialsFor(name)}
    </span>
  );
}
